import { AppBackground, HeaderWithBack, ResponsiveLogo, Text } from '@ui/components'
import React, { FC, useState, useEffect } from 'react'
import { FlatList, Image, View, TouchableOpacity, Dimensions } from 'react-native'
import Modal from 'react-native-modal'
import { Container, ModalContainer, ModalHeader } from './history.styles'
import { VaccineHistory } from '@front/apis'
import { useLocation, useNavigate } from 'react-router-dom'
import { ReactComponent as XSimpleIcon } from '@/assets/icons/x_simple_grey.svg'
import { Vaccine } from '@front/apis'
import { HeaderWithBackAena } from '@/components/headerwithback'
import { t } from 'i18next'

const formatDate = (d: string) => {
  const [dia, mes, anio] = d.split("/").map(Number);

  // En Date, los meses van de 0 a 11, por eso restamos 1
  return new Date(anio, mes - 1, dia);  }

// Formatea Date como dd/MM/yyyy
const formatAsDDMMYYYY = (date: Date): string => {
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = String(date.getFullYear())
  return `${dd}/${mm}/${yyyy}`
}

// Componente de tarjeta móvil para historial de vacunas
const MobileHistoryCard: FC<{
  item: VaccineHistory
  vaccine: Vaccine
  periodicity: string
  afterHowManyMonths: number
  onPress: (item: VaccineHistory) => void
}> = ({ item, onPress, vaccine, periodicity, afterHowManyMonths }) => {
  const fechaVacunacion = item.date ? formatAsDDMMYYYY(formatDate(item.date)) : ''
  const rabiaDate = formatAsDDMMYYYY(new Date(formatDate(item.date).getTime() + 21 * 24 * 60 * 60 * 1000))
  const fechaValidaViaje = item.vaccineName === 'Vacuna de la rabia' ? rabiaDate : t('common.na')
  const proximaVacuna = calculateRenewalDate(item, periodicity, afterHowManyMonths)
  const lote = item.ref ?? '—'

  return (
    <TouchableOpacity
      style={{
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 16,
        marginBottom: 8,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        width: '100%',
      }}
      onPress={() => onPress(item)}
    >
      <View style={{ gap: 8 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="caption" fontWeight="600" color="foreground0">
            {t('common.vaccinationdate')}
          </Text>
          <Text type="caption" color="primary">
            {t('common.photo')}
          </Text>
        </View>
        
        <Text type="caption" style={{ marginBottom: 4 }}>
          {fechaVacunacion}
        </Text>

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="caption" fontWeight="600" color="foreground0">
            {t('common.validforjourney')}
          </Text>
        </View>
        
        <Text type="caption" fontWeight={fechaValidaViaje !== t('common.na') ? "bold" : "normal"} style={{ marginBottom: 4 }}>
          {fechaValidaViaje}
        </Text>

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="caption" fontWeight="600" color="foreground0">
            {t('common.nextvaccine')}
          </Text>
        </View>
        
        <Text type="caption" style={{ marginBottom: 4 }}>
          {proximaVacuna ?? t('common.na')}
        </Text>

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="caption" fontWeight="600" color="foreground0">
            {t('common.lot')}
          </Text>
        </View>
        
        <Text type="caption">
          {lote}
        </Text>
      </View>
    </TouchableOpacity>
  )
}


const HistoryHeader: FC = () => {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-end',
        paddingVertical: 12,
        paddingHorizontal: 12,
        backgroundColor: '#F5F5F5',
        borderBottomWidth: 2,
        borderBottomColor: '#E0E0E0',
        marginTop: 8,
      }}
    >
      <View style={{ flex: 1.0, marginRight: 12 }}>
        <Text type="caption" numberOfLines={2} fontWeight="700" color="foreground0">
          {t('common.vaccinationdate')}
        </Text>
      </View>
      <View style={{ flex: 1.0, marginRight: 12 }}>
        <Text type="caption" numberOfLines={2} fontWeight="700" color="foreground0">
          {t('common.validforjourney')}
        </Text>
      </View>
      <View style={{ flex: 1.0, marginRight: 12 }}>
        <Text type="caption" fontWeight="700" color="foreground0">
          {t('common.nextvaccine')}
        </Text>
      </View>
      <View style={{ flex: 2, marginRight: 12 }}>
        <Text type="caption" fontWeight="700" color="foreground0">
          {t('common.lot')}
        </Text>
      </View>
      <View style={{ flex: 0.5 }}>
        <Text type="caption" fontWeight="700" color="foreground0">
          {t('common.photo')}
        </Text>
      </View>
    </View>
  )
}


  // Function to calculate renewal date based on last vaccination and periodicity
  function addOneYear(date: Date): Date {
    const next = new Date(date);
    next.setFullYear(next.getFullYear() + 1);
    return next;
  }
  const calculateRenewalDate = (vaccineItem: VaccineHistory, periodicity: string, afterHowManyMonths: number): string | null => {
    if (!vaccineItem.date) return null
    const baseDate = formatDate(vaccineItem.date)
    const baseDatePlus1Year = addOneYear(baseDate)

    switch (periodicity) {
      case 'ANNUAL':
        return formatAsDDMMYYYY(baseDatePlus1Year)
      case 'MONTHLY':
        return formatAsDDMMYYYY(new Date(baseDate.getTime() + afterHowManyMonths * 24 * 60 * 60 * 1000))
      case 'WEEKLY':
        return formatAsDDMMYYYY(new Date(baseDate.getTime() + afterHowManyMonths * 24 * 60 * 60 * 1000))
      case 'DAILY':
        return formatAsDDMMYYYY(new Date(baseDate.getTime() + afterHowManyMonths * 24 * 60 * 60 * 1000))
      case 'ONCE':
        return null
      default:
        return null
    }
  }

// Custom component for vaccine history items
// ACTUALIZADO: item como fila con columnas
const VaccineHistoryItem: FC<{
  item: VaccineHistory
  vaccine: Vaccine
  periodicity: string
  afterHowManyMonths: number
  onPress: (item: VaccineHistory) => void
}> = ({ item, onPress, vaccine, periodicity, afterHowManyMonths }) => {

  // Intentos de propiedades alternativas por si tu API usa otros nombres
  const fechaVacunacion = item.date ? formatAsDDMMYYYY(formatDate(item.date)) : ''
  const rabiaDate = formatAsDDMMYYYY(new Date(formatDate(item.date).getTime() + 21 * 24 * 60 * 60 * 1000))
  const fechaValidaViaje = item.vaccineName === 'Vacuna de la rabia' ? rabiaDate : t('common.na')
  const proximaVacuna = calculateRenewalDate(item, periodicity, afterHowManyMonths)
  const lote = item.ref ?? '—'

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 12,
        borderBottomWidth: 1,
        borderColor: '#e0e0e0',
        backgroundColor: 'white',
      }}
    >
      <View style={{ flex: 1.0, marginRight: 12 }}>
        <Text type="caption" numberOfLines={1} ellipsizeMode="tail">{fechaVacunacion}</Text>
      </View>

      <View style={{ flex: 1.0, marginRight: 12 }}>
        <Text type="caption" numberOfLines={1} ellipsizeMode="tail">{fechaValidaViaje}</Text>
      </View>

      <View style={{ flex: 1.0, marginRight: 12 }}>
        <Text type="caption" numberOfLines={1} ellipsizeMode="tail">{proximaVacuna ?? t('common.na')}</Text>
      </View>

      <View style={{ flex: 2, marginRight: 12 }}>
        <Text type="caption">{lote}</Text>
      </View>

      <TouchableOpacity style={{ flex: 0.5 }} onPress={() => onPress(item)}>
        <Text type="caption" color="primary">
          {t('common.view')}
        </Text>
      </TouchableOpacity>
    </View>
  )
}

export const VaccineHistoryPage: FC = () => {
  const { state } = useLocation() as {
    state: {
      tname: string
      vaccine: Vaccine
      history: VaccineHistory[]
      periodicity: string
      afterHowManyMonths: number
    }
  }
  const { history, tname, vaccine, periodicity, afterHowManyMonths } = state

  const [selectedItemImage, setSelectedItemImage] = React.useState<string>('')
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      const { width } = Dimensions.get('window')
      setIsMobile(width < 768)
    }

    checkScreenSize()
    const subscription = Dimensions.addEventListener('change', checkScreenSize)

    return () => subscription?.remove()
  }, [])

  const handleItemPress = (item: VaccineHistory) => {
    console.log('handleItemPress called with item:', item)
    console.log('item.file:', item.file)
    console.log('item.file type:', typeof item.file)
    console.log('item.file length:', item.file?.length)
    setSelectedItemImage(item.file)
  }

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
            <ResponsiveLogo
              breakpoint={820}
              hysteresis={20}
              large={{ width: 680, height: 96 }}
              small={{ width: 360, height: 48 }}
            />
        </View>
        <HeaderWithBackAena title={t('common.vaccineshistory')} />
      <Container>
        <Text type="H5" style={{ marginBottom: 10 }}>
          {t(`vaccines.${tname}`)}
        </Text>
        {isMobile ? (
          <View>
            {history.map((item, index) => (
              <MobileHistoryCard
                key={index.toString()}
                item={item}
                onPress={handleItemPress}
                vaccine={vaccine}
                periodicity={periodicity}
                afterHowManyMonths={afterHowManyMonths}
              />
            ))}
          </View>
        ) : (
          <FlatList
            data={history}
            ListHeaderComponent={<HistoryHeader />}    
            keyExtractor={(item, index) => index.toString()}
            renderItem={({ item }) => (
              <VaccineHistoryItem item={item} onPress={handleItemPress} vaccine={vaccine} periodicity={periodicity} afterHowManyMonths={afterHowManyMonths} />
            )}
          />
        )}
      </Container>
      <Modal
        backdropOpacity={0.7}
        useNativeDriver
        useNativeDriverForBackdrop
        isVisible={!!selectedItemImage}
        onBackdropPress={() => setSelectedItemImage('')}
        onBackButtonPress={() => setSelectedItemImage('')}
        style={{ margin: 20, justifyContent: 'center', alignItems: 'center' }}>
        <ModalContainer>
          <ModalHeader>
            <Text type="H5">{t('common.preview')}</Text>
            <TouchableOpacity onPress={() => setSelectedItemImage('')}>
              <XSimpleIcon width={30} height={30} />
            </TouchableOpacity>
          </ModalHeader>
          {!!selectedItemImage && (
            <View style={{ width: '100%', height: 300, backgroundColor: 'lightgray', justifyContent: 'center', alignItems: 'center' }}>
              <Image 
                source={{ uri: selectedItemImage }} 
                style={{ width: '100%', height: 300 }} 
                resizeMode="contain"
                onError={(error) => console.log('Image loading error:', error)}
                onLoad={() => console.log('Image loaded successfully')}
              />
            </View>
          )}
        </ModalContainer>
      </Modal>
      </View>
    </AppBackground>
  )
}
